<template>
  <div>
    <div v-if="!isShowTertiarydataonly">
      <SearchNav 
        :currentSearch="currentSearch"
        :searchBar="showSearch"
        fallbackUrl="/take-5-daily"
        layoutId="t5d"
      />
    </div>
    <hr class="border-t-top border-t-gray-300 absolute left-0 w-full md:w-[80vw]" :class="!isShowTertiarydataonly ? '' :'top-0.5'" aria-hidden="true">

    <!-- Primary Article (Featured/Hero) -->
    <section v-if="!isShowTertiarydataonly"
      class="relative before:content-[''] before:absolute before:right-0 before:bottom-[0.7857rem] before:h-full before:w-[200vw] md:p-12 md:p-12 py-5 px-6"
    >
      <div
        v-if="articles.primary"
        class="relative overflow-hidden rounded-[0.5714285714rem] transition-all duration-200 ease-in-out group hover:shadow-[0.714rem_0.714rem_1.143rem_-0.429rem_var(--color-gray-600)] h-[32.143rem] flex items-end focus-element"
      >
        <BackgroundImage
          animate
          gradient
          :image="articles.primary.image ?? ''"
          :label="articles.primary.title ?? ''"
        />

          <FavoriteButton v-if="articles.primary.takeFiveId"
          :logged-in="userStore.loggedIn"
          :item-type="articles.primary.type"
          :is-favorite="isFavorite(String(articles.primary.takeFiveId))"
          @toggle="toggleFavorite(articles.primary)"
          id="fav-primary-item"
          />

        <NuxtLink external id="primary-item" :aria-label="articles.primary.title" :to="articles.primary.url ?? ''" class="relative block w-full h-full p-[2.143rem] text-white z-10">
          <div class="absolute bottom-[2.1429rem] flex flex-col items-start w-[calc(100%-4.285714rem)]">
            <div
              class="h-[1.7142857rem] bg-gray-900 rounded-[0.4285714286rem] mb-[0.7142857rem] pt-[0.1rem] pb-[0.1rem]  px-[0.6428571rem]"
            >
              <Icon icon="newspaper" class="text-green-300 text-lg" v-if="articles.primary.type === 'article'"/>
              <Icon icon="video" class="text-sky text-lg" v-if="articles.primary.type === 'video'"/>
              <Icon icon="podcast" class="text-pink text-lg" v-if="articles.primary.type === 'podcast'"/>
            </div>
            <div class="w-full">
              <div class="mb-[0.7142857rem]">
                <div class="text-[1.7142857rem] font-normal leading-110 tracking-[-0.035714rem] text-white">
                  {{ articles.primary.title }}
                </div>
             </div>
              <div class="pb-[0.7142857rem] pt-[0.071rem]" v-if="articles.primary.type === 'podcast' && articles.primary.audioLink">
              <AudioPlayer 
                id="primary-item-audio"
                class="mb-4"
                :src="articles.primary.audioLink"
                color="white"
              />
              </div>
              <ArticleMeta
                class="text-[1rem] !mb-[1.125rem]"
                :publishAt="articles.primary.publishAt ?? undefined"
                :authorName="articles.primary.authorName ?? undefined"
                :isFeatured="true"
                 titlePos='bottom'
              />
            </div>
          </div>
        </NuxtLink>
      </div>
    </section>

    <hr v-if="articles.primary && !isShowTertiarydataonly" class="border-t-top border-t-gray-300 absolute left-0 w-full md:w-[80vw]" aria-hidden="true">

    <!-- Secondary Articles -->
    <section
      v-if="articles.secondary.length && !isShowTertiarydataonly"
      class="grid md:grid-cols-2 gap-6 relative before:content-[''] z-[1] before:absolute before:right-0 before:bottom-[1.5rem] before:h-full before:w-[200vw] before:border-b-[0.0714285714rem] before:border-black/5 before:pointer-events-none before:translate-y-[1.57rem] lg:p-12 md:p-12 py-5 px-6"
    >
      <MediaCard
        v-for="(article, index) in articles.secondary"
        :key="index"
        :article="article"
        :titlePosition="titlePosition ?? 'bottom'"
        @toggle-favorite="toggleFavorite"
        :isFavorite="isFavorite(String(article.takeFiveId))"
        :id="`sec-item-${index}`"
      />
    </section>
    <hr v-if="articles.secondary.length && !isShowTertiarydataonly" class="border-t-top border-t-gray-300 absolute left-0 w-full md:w-[80vw]" aria-hidden="true">

    <!-- Tertiary Articles -->
    <section v-if="articles.tertiary.length" :class="!isShowTertiarydataonly ? 'lg:ml-12 lg:pl-0 lg:p-12 md:p-12 py-5 px-6 lg:overflow-hidden' :''">
      <div
        v-for="(article, index) in articles.tertiary"
        :key="index"
        class="flex flex-col md:flex-row gap-6 before:right-0 sm:before:right-[-2rem] first:border-0 border-t border-gray-200 relative before:z-[1] before:content-[''] before:absolute before:bottom-[0.79rem] before:h-full before:w-[200vw] before:border-b-[0.0714285714rem] before:border-black/5 before:pointer-events-none before:translate-y-[0.86rem] md:pb-3 lg:px-0 pt-6"
        :class="index === articles.tertiary.length - 1 ? 'before:hidden' : ''"
      >
        <div
          class="relative overflow-hidden rounded-[0.5714rem] transition-all duration-200 ease-in-out group hover:shadow-[0.714rem_0.714rem_1.143rem_-0.429rem_var(--color-gray-600)] md:w-1/3 min-h-[10.7143rem] lg:max-w-[21rem] lg:max-h-[10.714285rem] w-full focus-element" :class="!isShowTertiarydataonly ?'' :'lg:max-w-[22.2857rem]'"
       >
          <BackgroundImage animate gradient :image="article.image" :label="article.title" />
          
          <FavoriteButton
          :logged-in="userStore.loggedIn"
          :item-type="article.type"
          :is-favorite="isFavorite(String(article.takeFiveId))"
          @toggle="toggleFavorite(article)"
          :id="`tertiary-fav-item-${index}`"
          />

          <NuxtLink
            external
            :id="`tertiary-img-${index}`"
            :aria-label="article.title"
            :to="article.url ?? ''"
            class="absolute inset-0 bg-black/20 hover:bg-black/40 transition-all p-[1.0714286rem] flex items-end z-10"
          >
            <div class="absolute bottom-[1rem] flex flex-col items-start">
              <div
                class="bg-gray-900 rounded-[0.4285714286rem] pt-[0.1rem] pb-[0.1rem] pl-[0.6rem] pr-[0.6rem]"
              >
                <Icon icon="newspaper" class="text-green-300 text-lg" v-if="article.type === 'article'"/>
                <Icon icon="video" class="text-sky text-lg" v-if="article.type === 'video'"/>
                <Icon icon="podcast" class="text-pink text-lg" v-if="article.type === 'podcast'"/>
              </div>
            </div>
          </NuxtLink>
        </div>

        <div class="md:w-2/3 pb-3 lg:min-h-[12.357rem]">
           <Heading
            :level="5"
            class="text-[1.29rem] font-medium leading-[1.45rem] mb-2" color="text-ash-500"
          >
            <NuxtLink 
              :id="`tertiary-item-${index}`" 
              :to="article.url ?? ''"
              external
            >
              {{ article.title }}
            </NuxtLink>
          </Heading>
          <ArticleExcerpt
            :description="article.excerpt ?? ''"
            :maxLength="200"
          />
          <hr v-if="article.type !== 'podcast'"
            class="my-[1.5rem] border-t border-t-gray-300" aria-hidden="true"
          />
          <ArticleMeta
            :publishAt="article.publishAt ?? undefined"
            :authorName="article.authorName ?? undefined"
            :isFeatured="false"
            titlePos="bottom"
          />

          <AudioPlayer
            :id="`tertiary-audio-${index}`"
            v-if="article.type === 'podcast' && article.audioLink"
            class="mb-4 mt-4"
            :src="article.audioLink"
            color="black"
          />
        </div>
      </div>

    </section>
    <hr v-if="articles.tertiary.length && !isShowTertiarydataonly" class="border-t-top border-t-gray-300 absolute left-0 w-full md:w-[80vw] pt-[2.25rem] md:pt-0" aria-hidden="true"/>

    <!-- No Content Message -->
    <section 
      v-if="!articles.primary && !articles.secondary.length && !articles.tertiary.length && !isShowTertiarydataonly"
      class="text-center py-20 px-4"
    >
      <p class="text-gray-500 text-lg" role="alert" aria-atomic="true">
        <span v-if="currentSearch">No results found for "{{ currentSearch }}". Try a different search term.</span>
        <span v-else>No content available at this time.</span>
      </p>
    </section>

    <Pagination v-if="!isShowTertiarydataonly" class="mt-12" 
     :initialPage="currentPage" 
     :showPageNumbers="showPageNumbers"
     :totalCount="totalDisplayed"
     :hasNext="hasNext"
     :hasPrev="hasPrev"
     :limit="limit"
     :totalPages="totalPages"
     />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'

import MediaCard from '../../organism/take-five-daily/mediaCard.vue'
import ArticleExcerpt from '../../organism/article/excerpt.vue'
import ArticleMeta from '../../organism/article/meta.vue'
import BackgroundImage from '../../atom/backgroundImage.vue'
import Heading from '../../atom/heading.vue'
import Icon from '../../atom/icon.vue'
import SearchNav from '../../organism/take-five-daily/searchNav.vue'
import Pagination from '../../organism/pagination.vue'
import AudioPlayer from '../../atom/audioPlayer.vue'
import FavoriteButton from '../../atom/FavoriteButton.vue'

import { useUserStore } from '~/stores/userStore'
import useSiteService from '~/composables/siteService'
import { URL_CONFIG } from '~~/shared/utils/url.const'
import { CONTENT_TYPES } from '~~/shared/utils/contentTypes'
const { getFavorites, favoriteItem, unfavoriteItem } = useSiteService()

const route = useRoute()
const userStore = useUserStore();

export interface Article {
  title?: string | undefined
  url?: string | null
  image?: string | null
  excerpt?: string | null
  meta?: string | null
  publishAt?: string | null
  authorName?: string | null
  type?: 'article' | 'video' | 'podcast' | 'mixed'
  audioLink?: string
  videoLink?: string,
  takeFiveId?: string | number
}

export interface ArticlePageProps {
  primary?: Article | null
  secondary?: Article[] | null
  tertiary?: Article[] | null
  titlePosition?: string | 'bottom'
  totalPages?: number
  currentPage?: number
  limit?: number
  showSearch?: boolean
  currentSearch?: string
  hasNext?: boolean
  hasPrev?: boolean
  showPageNumbers?: boolean 
}

const props = withDefaults(
  defineProps<ArticlePageProps & {
    isShowTertiarydataonly?: boolean
  }>(),
  {
    primary: null,
    secondary: () => [],
    tertiary: () => [],
    titlePosition: 'bottom',
    totalPages: undefined,
    currentPage: 1,
    limit: 10,
    showSearch: false,
    currentSearch: '',
    isShowTertiarydataonly: false,
    hasNext: undefined,
    hasPrev: undefined,
    showPageNumbers: true
  }
)

type FavoriteType = 'article' | 'podcast'

const favoriteIds = ref<string[]>([])
const favoriteType = ref<FavoriteType>(CONTENT_TYPES.ARTICLE)

onMounted(async () => {
  try {
    if (route.path === URL_CONFIG.TAKE_FIVE) {
      const [articles, podcasts]: any = await Promise.all([
        getFavorites(CONTENT_TYPES.ARTICLES),
        getFavorites(CONTENT_TYPES.PODCAST)
      ])
      
      const articlesArray = Array.isArray(articles) ? articles : []
      const podcastsArray = Array.isArray(podcasts) ? podcasts : []
      
      favoriteIds.value = [...articlesArray, ...podcastsArray]
    }
    else if (route.path.includes(URL_CONFIG.TAKE_FIVE_PODCASTS)) {
      const podcasts: any = await getFavorites(CONTENT_TYPES.PODCAST)
      favoriteIds.value = Array.isArray(podcasts) ? podcasts : []
    }
    else if (route.path.includes(URL_CONFIG.TAKE_FIVE_ARTICLES)) {
      const articles: any = await getFavorites(CONTENT_TYPES.ARTICLES)
      favoriteIds.value = Array.isArray(articles) ? articles : []
    }
  } catch (err) {
    favoriteIds.value = []
  }
})

const isFavorite = (id?: string) => {
  if (id == null) return false
  
  if (!Array.isArray(favoriteIds.value)) {
    favoriteIds.value = []
    return false
  }

  return favoriteIds.value.includes(id)
}

const toggleFavorite = async (item: any) => {
  const itemId = item?.takeFiveId || item?.id
  
  favoriteType.value = item?.type === CONTENT_TYPES.PODCAST ? CONTENT_TYPES.PODCAST : CONTENT_TYPES.ARTICLE
  
  if (!itemId) {
    return
  }
  
  if (!Array.isArray(favoriteIds.value)) {
    favoriteIds.value = []
  }
  
  const normalizedId = itemId
  
  try {
    if (isFavorite(itemId)) {
      favoriteIds.value = favoriteIds.value.filter(i => i !== normalizedId)
      await unfavoriteItem(favoriteType.value, itemId)
    } else {
      favoriteIds.value.push(normalizedId)
      await favoriteItem(favoriteType.value, itemId)
    }
  } catch (err) {
    if (isFavorite(itemId)) {
      favoriteIds.value = favoriteIds.value.filter(i => i !== normalizedId)
    } else {
      favoriteIds.value.push(normalizedId)
    }
  }
}

const articles = computed(() => {
  return {
    primary: props.primary ?? null,
    secondary: Array.isArray(props.secondary) ? props.secondary : [],
    tertiary: Array.isArray(props.tertiary) ? props.tertiary : []
  }
})

const totalDisplayed = computed(() => {
  return (articles.value.primary ? 1 : 0) + 
         articles.value.secondary.length + 
         articles.value.tertiary.length
})

const startItem = computed(() => {
  return (props.currentPage - 1) * props.limit + 1
})

const endItem = computed(() => {
  return startItem.value + totalDisplayed.value - 1
})
const titlePosition = computed<'top' | 'bottom'>(() =>
  props.titlePosition === 'top' ? 'top' : 'bottom'
)

const isShowTertiarydataonly = ref(false)

watch(
  () => props.isShowTertiarydataonly,
  (val) => {
    isShowTertiarydataonly.value = val
  },
  { immediate: true }
)

</script>
 